// Finalized treatment areas produced by the Treatment Area Builder (Run Step 4).
// A weekly run usually has several areas (one per notice); each is saved here and
// consumed by Run Step 5 (publish to AGOL) and Step 8 (mark ORIGINAL subgrids treated).

const KEY = "pioneer_treatment_areas";

/** Everything about a notice's appearance that isn't derivable from the polygon.
 *
 * Saved with the area so revisiting one restores the notice exactly as it was, and so
 * batch-exporting every notice stamps each area's own road labels rather than reusing
 * whichever labels happened to be on screen. Optional — areas saved before this existed
 * simply have no layout to restore.
 */
export interface NoticeLayout {
  roads: { text: string; lat: number; lng: number; rotation: number }[];
  customBody: string | null;
  basemap: string;
  mapTemplate: "community" | "spray";
  /** Notice mini-map view, so manual pans/zooms survive a round-trip */
  center: [number, number] | null;
  zoom: number | null;
}

export interface TreatmentArea {
  id: string;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  gridlabels: string[]; // original picked subgrids — used for Step 8 writeback
  date: string;
  timeWindow: string;
  reason: "wnv" | "abundance";
  acres: number;
  savedAt: string;
  notice?: NoticeLayout;
  /** OBJECTID of this area's feature on the AGOL treatment layer, once it has been
   *  written there. Correlates a local record to its remote row across reloads, and
   *  makes re-saving an edit an update rather than a duplicate. */
  agolObjectId?: number | null;
  /** Last known Status on AGOL: 'Draft' while curating, 'Upcoming' once scheduled. */
  agolStatus?: string | null;
  /** Set when the AGOL write failed, so the UI can show the area is local-only. */
  syncError?: string | null;
}

export function loadTreatmentAreas(): TreatmentArea[] {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** Insert or (when the id already exists) replace a saved area. */
export function upsertTreatmentArea(area: TreatmentArea): TreatmentArea[] {
  const list = loadTreatmentAreas();
  const i = list.findIndex((a) => a.id === area.id);
  if (i >= 0) list[i] = area;
  else list.push(area);
  localStorage.setItem(KEY, JSON.stringify(list));
  return list;
}

export function deleteTreatmentArea(id: string): TreatmentArea[] {
  const list = loadTreatmentAreas().filter((a) => a.id !== id);
  localStorage.setItem(KEY, JSON.stringify(list));
  return list;
}

/** YYYY-MM-DD (the date input's format) -> MM/DD/YYYY, which is what the treatment layer,
 *  the shapefile schema, and the website popups all expect. TreatmentDate is free text on
 *  AGOL, so sending ISO through would be accepted silently and then render wrong. */
export function isoToMdy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(iso || "");
}

/** MM/DD/YYYY (how AGOL stores TreatmentDate) -> YYYY-MM-DD for date inputs.
 *  Returns "" when unparseable, so callers can decide on a fallback. */
export function mdyToIso(s: string | null | undefined): string {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s || "").trim());
  return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : "";
}

export function areaLabel(a: TreatmentArea): string {
  return a.gridlabels[0] ? `${a.gridlabels[0]}${a.gridlabels.length > 1 ? ` +${a.gridlabels.length - 1}` : ""}` : "custom";
}
